import { Directive, ElementRef, EventEmitter, NgZone, OnDestroy, OnInit, Output } from '@angular/core';

export class ResizedEvent {
  public newRect: DOMRectReadOnly;
  public oldRect?: DOMRectReadOnly;
  public isFirst: boolean;

  public constructor(newRect: DOMRectReadOnly, oldRect: DOMRectReadOnly | undefined) {
    this.newRect = newRect;
    this.oldRect = oldRect;
    this.isFirst = !oldRect;
  }
}

@Directive({
  selector: '[fwResized]',
  standalone: true,
})
export class FwResizedDirective implements OnInit, OnDestroy {
  @Output()
  public readonly fwResized = new EventEmitter<ResizedEvent>();
  private observer: ResizeObserver;
  private oldRect?: DOMRectReadOnly;
  private debounceTimeout: number | null = null;
  private readonly debounceMs = 16;

  public constructor(
    private readonly element: ElementRef,
    private readonly zone: NgZone,
  ) {
    this.observer = new ResizeObserver(entries => this.handleResize(entries));
  }

  public ngOnInit(): void {
    this.observer.observe(this.element.nativeElement);
  }

  public ngOnDestroy(): void {
    this.observer.disconnect();
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
  }

  private handleResize(entries: ResizeObserverEntry[]): void {
    // Debounce resize events for better performance
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = window.setTimeout(() => {
      this.zone.run(() => this.observe(entries));
    }, this.debounceMs);
  }

  private observe(entries: ResizeObserverEntry[]): void {
    const domSize = entries[0];
    const newRect = domSize.contentRect;

    // Skip if dimensions haven't changed
    if (this.oldRect &&
        this.oldRect.width === newRect.width &&
        this.oldRect.height === newRect.height) {
      return;
    }

    const resizedEvent = new ResizedEvent(newRect, this.oldRect);
    this.oldRect = newRect;
    this.fwResized.emit(resizedEvent);
  }
}
